import * as React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  FieldError,
  Fieldset,
  FlexRow,
  Form,
  H2,
  Input,
  Label,
  Link,
  RequiredText,
  Select,
  Stack,
  Text,
  TextArea,
  UnexpectedError,
} from "atoms";
import { constants } from "config";
import { scrollToTop } from "utils/scroll";
import { ImageUpload } from "features/images";
import { fieldOptions } from "utils/form";
import { countries } from "features/reviewable";
import { useAuth } from "features/auth";
import { useSubmissionForm } from "../api/useSubmissionForm";
import { SubmissionArtistName } from "./SubmissionArtistName";
import type { OpenCall, SubmissionArtistDetails } from "../types";

// 100mb = mb * kb * b
const MAX_SUBMISSION_SIZE = 100 * 1000 * 1000;

const textAreaRequirements = {
  ...fieldOptions.maxLength(2000),
};

const titleRequirements = {
  ...fieldOptions.maxLength(80),
  ...fieldOptions.required,
};

type SubmissionFormProps = {
  openCall: OpenCall;
  artistDetails: SubmissionArtistDetails;
  heading?: string;
  showContinue?: boolean;
} & Omit<Parameters<typeof useSubmissionForm>[0], "openCallId">;

function SubmissionForm({
  artistDetails,
  showContinue,
  heading,
  onSuccess,
  openCall,
  submission,
}: SubmissionFormProps) {
  const {
    roles: { showAdminFeatures },
  } = useAuth();

  const {
    control,
    formState: { errors },
    isEdit,
    mutation,
    onSubmit,
    register,
    reset,
  } = useSubmissionForm({
    openCallId: openCall.id,
    onSuccess,
    submission: submission
      ? { name: artistDetails.name, ...submission }
      : undefined,
  });

  const showNameField = showAdminFeatures && isEdit;

  React.useEffect(scrollToTop, [mutation.isSuccess]);

  // HACK: This conditionally renders the ImageUpload component because there is no way to reset
  // the field using useForm's `reset` method, so instead we'll unmount and remount it to get its
  // original state (i.e. blank file input and no upload preview)
  const [showImgUploader, setShowImgUploader] = React.useState(true);

  React.useEffect(() => {
    if (!submission && mutation.isSuccess && mutation.data) {
      // Reset the form so user can submit another, if able. Keep the name, country, and
      // checkbox values they submitted so they don't have to enter them again, but clear
      // the rest. If the user is unable to submit another, they won't see the form.
      const { countryOfResidence, name } = mutation.data;
      reset({
        affirmOriginal: true,
        affirmUsage: true,
        countryOfResidence,
        name,
        title: "",
        submission: { url: "" },
        voterSubmissionPromptResponse: openCall.voterSubmissionPrompt
          ? ""
          : undefined,
        submissionPromptResponse: openCall.submissionPrompt ? "" : undefined,
      });
      // Manually reset ImageUpload field, see HACK above
      setShowImgUploader(false);
      const timeout = setTimeout(() => setShowImgUploader(true), 1);
      return () => clearTimeout(timeout);
    }
    return noop;
  }, [
    mutation.isSuccess,
    mutation.data,
    submission,
    reset,
    openCall.voterSubmissionPrompt,
    openCall.submissionPrompt,
  ]);

  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  return (
    <Form onSubmit={onSubmit}>
      {heading && <H2 size="lg">{heading}</H2>}

      <Fieldset>
        {showNameField ? (
          <Label>
            Artist Name
            <RequiredText />
            <Input {...register("name", titleRequirements)} />
            {errors.name && <FieldError>{errors.name.message}</FieldError>}
          </Label>
        ) : (
          <Stack>
            Artist Name
            <SubmissionArtistName
              artistName={submission?.name}
              {...artistDetails}
            />
            <Text size="xxs">
              If you would like to change your name for your submission, please
              contact{" "}
              <Link href={`mailto:${constants.supportEmail}`}>support</Link>.
            </Text>
          </Stack>
        )}
      </Fieldset>

      {showImgUploader && (
        <ImageUpload
          control={control}
          fieldName="submission"
          required
          errorMessage={errors.submission?.url?.message}
          accept="both"
          maxFileSize={MAX_SUBMISSION_SIZE}
          src={submission?.submission}
          autoFocus
          label={
            <>
              Upload Artwork
              <RequiredText />
            </>
          }
        />
      )}

      <Label>
        Title of Artwork
        <RequiredText />
        <Input {...register("title", titleRequirements)} />
        {errors.title && <FieldError>{errors.title.message}</FieldError>}
      </Label>

      {!!openCall.submissionPrompt?.length && (
        <Label>
          {openCall.submissionPrompt}
          <small>
            Your response will be visible to HUG during the selection process.
            It will not be visible during the voting process.
          </small>
          <TextArea
            {...register("submissionPromptResponse", textAreaRequirements)}
          />
          {errors.submissionPromptResponse && (
            <FieldError>{errors.submissionPromptResponse.message}</FieldError>
          )}
        </Label>
      )}

      {!!openCall.voterSubmissionPrompt?.length && (
        <Label>
          {openCall.voterSubmissionPrompt}
          <small>
            Your response will be visible during the voting process.
          </small>
          <TextArea
            {...register("voterSubmissionPromptResponse", textAreaRequirements)}
          />
          {errors.voterSubmissionPromptResponse && (
            <FieldError>
              {errors.voterSubmissionPromptResponse.message}
            </FieldError>
          )}
        </Label>
      )}

      {!!openCall.requireCountryOfResidence && (
        <Label>
          Country of Residence
          <RequiredText />
          <Select
            {...register("countryOfResidence", fieldOptions.required)}
            isInvalid={!!errors.countryOfResidence}
            aria-invalid={!!errors.countryOfResidence}
          >
            <option value="">Choose country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </Select>
        </Label>
      )}

      {!isEdit && (
        <Stack gap="sm">
          <Label as="div">
            <SCheckbox
              {...register("affirmOriginal", fieldOptions.required)}
              label="I affirm that the work submitted is my original creation, and I
                have the rights needed to submit it to this Open Call."
            />
            {errors.affirmOriginal && (
              <FieldError>
                You must agree in order to submit your artwork.
              </FieldError>
            )}
          </Label>

          <Label as="div">
            <SCheckbox
              {...register("affirmUsage", fieldOptions.required)}
              label="In the event that I am selected, I grant HUG and any relevant
                partners the rights to use or display this artwork solely for the
                purposes of this Open Call. I acknowledge that my artwork may be
                subject to cropping or resizing to fit the display."
            />
            {errors.affirmUsage && (
              <FieldError>
                You must agree in order to submit your artwork.
              </FieldError>
            )}
          </Label>
        </Stack>
      )}

      <FlexRow justifyContent="center">
        {isEdit && (
          <Link variant="secondary" onClick={goBack}>
            Cancel
          </Link>
        )}
        <Button type="submit" variant="primary" disabled={mutation.isLoading}>
          {isEdit ? "Update" : `Submit${showContinue ? " and Continue" : ""}`}
        </Button>
      </FlexRow>
      {mutation.error && <UnexpectedError error={mutation.error} />}
    </Form>
  );
}
const SCheckbox = styled(Checkbox)(({ theme }) => ({
  "> div": {
    fontSize: theme.fontSizes.xxs,
    lineHeight: 1.25,
    position: "relative",
  },
}));

export { SubmissionForm };
